import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Grid, MenuItem, Button, Typography, Box, CircularProgress, Card, CardContent, Chip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { Edit as EditIcon, Close as CloseIcon } from '@mui/icons-material';
import { getPracticeConcerns, getConcernQuestions, getApiBaseUrl } from '../services/patientService';

// Fallback concern options for when dynamic loading fails or no practice is selected
const FALLBACK_CONCERN_OPTIONS = [
  "Suspicious skin lesion(s) or Skin Cancer",
  "Face Appearance",
  "Breast Appearance",
  "Nose Appearance",
  "Body Appearance",
  "Breast Reduction",
  "Facial Reconstruction",
  "Non-healing wound",
  "Breast Disease/Cancer",
  "Burn or Scarring",
  "Reconstruct after trauma"
];

// Fallback initial questions based on concern types (for backward compatibility)
const FALLBACK_INITIAL_QUESTIONS = {
  "Suspicious skin lesion(s) or Skin Cancer": [
    "When did you first notice the lesion?",
    "Has the lesion changed in size, shape, or color?",
    "Is the lesion painful, itchy, or bleeding?",
    "Have you had any previous skin cancers or pre-cancerous lesions?",
    "Is there a family history of skin cancer?"
  ],
  "Face Appearance": [
    "What specific aspects of your facial appearance concern you?",
    "How long have you been concerned about this?",
    "How does this concern affect your daily life?",
    "Have you tried any treatments or procedures before?"
  ],
  "Breast Appearance": [
    "What specific aspects of your breast appearance concern you?",
    "When did you first notice this issue?",
    "Have you experienced any pain or discomfort?",
    "Have you had any previous breast procedures or surgeries?"
  ],
  "Non-healing wound": [
    "Where is the wound located?",
    "How long have you had this wound?",
    "What treatments have you tried?",
    "Is there any drainage, odor, or increasing pain?"
  ]
  // Add more concern-specific questions as needed
};

// Default questions for any concern type not specifically listed above
const DEFAULT_QUESTIONS = [
  "Can you describe your concern in more detail?",
  "When did you first notice this issue?",
  "On a scale of 1-10, how would you rate any associated discomfort?",
  "What makes your symptoms better or worse?",
  "How does this affect your daily activities?"
];

export default function Step4Concerns({ formData, onChange, fieldErrors = {} }) {
  const { t } = useTranslation('concerns');
  const { t: tCommon } = useTranslation('common');
  const [isGenerating, setIsGenerating] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [userResponses, setUserResponses] = useState({});
  const [currentInput, setCurrentInput] = useState('');
  const [narrative, setNarrative] = useState('');
  const [subjectiveText, setSubjectiveText] = useState('');

  // Dialog state for editing concerns
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [dialogConcerns, setDialogConcerns] = useState([]);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [followUpQuestionsGenerated, setFollowUpQuestionsGenerated] = useState(false);

  // Dynamic concerns state
  const [concernOptions, setConcernOptions] = useState(FALLBACK_CONCERN_OPTIONS);
  const [dynamicQuestions, setDynamicQuestions] = useState({});
  const [loadingConcerns, setLoadingConcerns] = useState(false);
  const [concernsError, setConcernsError] = useState(null);

  // Load practice-specific concerns when practice changes
  useEffect(() => {
    const loadPracticeConcerns = async () => {
      if (!formData.practice) {
        console.log('No practice selected, using fallback concerns');
        setConcernOptions(FALLBACK_CONCERN_OPTIONS);
        return;
      }

      try {
        setLoadingConcerns(true);
        setConcernsError(null);
        console.log(`Loading concerns for practice: ${formData.practice}`);
        
        const concerns = await getPracticeConcerns(formData.practice);
        
        if (concerns && concerns.length > 0) {
          const concernNames = concerns.map(concern => concern.concern_name);
          setConcernOptions(concernNames);
          console.log(`✅ Loaded ${concerns.length} concerns for ${formData.practice}`);
          
          // Also load questions for each concern
          await loadQuestionsForConcerns(concerns);
        } else {
          console.log('No concerns found for practice, using fallback');
          setConcernOptions(FALLBACK_CONCERN_OPTIONS);
        }
      } catch (error) {
        console.error('Failed to load practice concerns:', error);
        setConcernsError(error.message);
        setConcernOptions(FALLBACK_CONCERN_OPTIONS);
      } finally {
        setLoadingConcerns(false);
      }
    };

    loadPracticeConcerns();
  }, [formData.practice]);

  // Load questions for each concern
  const loadQuestionsForConcerns = async (concerns) => {
    try {
      const questionsMap = {};
      
      for (const concern of concerns) {
        try {
          const questions = await getConcernQuestions(concern.id);
          if (questions && questions.length > 0) {
            // Convert to the format expected by the component
            questionsMap[concern.concern_name] = questions
              .filter(q => q.question_type === 'initial') // Only load initial questions for now
              .sort((a, b) => a.display_order - b.display_order)
              .map(q => q.question_text);
          }
        } catch (error) {
          console.error(`Failed to load questions for concern ${concern.concern_name}:`, error);
          // Continue with other concerns even if one fails
        }
      }
      
      setDynamicQuestions(questionsMap);
      console.log('✅ Loaded dynamic questions for concerns:', Object.keys(questionsMap));
    } catch (error) {
      console.error('Error loading questions for concerns:', error);
    }
  };

  // Initialize conversation when concerns are selected
  // Debug existing narrative in formData
  useEffect(() => {
    console.log('Form data in component:', formData);
    if (formData.medical_narrative) {
      console.log('Existing narrative found:', formData.medical_narrative);
      setNarrative(formData.medical_narrative);
      // Populate Subjective from existing narrative
      const subj = extractSubjective(formData.medical_narrative);
      setSubjectiveText(subj);
    }
  }, []);

  useEffect(() => {
    if (formData.concerns?.length > 0) {
      console.log('Concerns selected, initializing conversation');
      const initialMessages = formData.concerns.flatMap(concern => {
        const concernQuestions = dynamicQuestions[concern] || FALLBACK_INITIAL_QUESTIONS[concern] || DEFAULT_QUESTIONS;
        return concernQuestions.map((q, i) => ({
          type: 'question',
          text: q,
          concern,
          questionIndex: i
        }));
      });
      console.log('Generated questions:', initialMessages);
      setConversation(initialMessages);
      setCurrentQuestion(0);
      setUserResponses({});
    }
  }, [formData.concerns, dynamicQuestions]);

  // Function to generate dynamic follow-up questions based on previous responses
  const generateFollowUpQuestions = async (responses) => {
    try {
      setIsGeneratingQuestions(true);
      console.log('Generating follow-up questions based on previous responses');
      
      console.log('Sending follow-up questions request to backend...');
      
      // Call secure backend endpoint instead of OpenAI directly
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/openai/generate-followup-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          formData,
          conversation,
          responses
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend API Error:', errorData);
        throw new Error(errorData.error || 'Failed to generate follow-up questions');
      }
      
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to generate follow-up questions');
      }
      
      const data = result.data;
      console.log('Follow-up questions API response:', data);
      
      try {
        // Parse the response and extract the follow-up questions
        const content = data.choices[0]?.message?.content;
        
        // Extract the JSON part from the response
        const jsonMatch = content.match(/\{[\s\S]*\}/);
        if (!jsonMatch) throw new Error('Could not parse follow-up questions JSON');
        
        const followUpQuestions = JSON.parse(jsonMatch[0]);
        console.log('Parsed follow-up questions:', followUpQuestions);
        
        // Add follow-up questions to the conversation
        let updatedConversation = [...conversation];
        let questionCount = conversation.length;
        
        Object.entries(followUpQuestions).forEach(([concern, questions]) => {
          if (Array.isArray(questions)) {
            questions.forEach((question, idx) => {
              updatedConversation.push({
                type: 'follow-up',
                text: question,
                concern,
                questionIndex: 1000 + idx // Use a high index to distinguish follow-up questions
              });
              questionCount++;
            });
          }
        });
        
        console.log('Updated conversation with follow-up questions:', updatedConversation);
        setConversation(updatedConversation);
        setFollowUpQuestionsGenerated(true);
        
        return true;
      } catch (error) {
        console.error('Error parsing follow-up questions:', error);
        return false;
      }
    } catch (error) {
      console.error('Error generating follow-up questions:', error);
      return false;
    } finally {
      setIsGeneratingQuestions(false);
    }
  };

  const handleResponseSubmit = () => {
    if (!currentInput.trim()) return;
    
    const currentQ = conversation[currentQuestion];
    const updatedResponses = {
      ...userResponses,
      [currentQ.concern]: {
        ...userResponses[currentQ.concern],
        [currentQ.questionIndex]: currentInput
      }
    };
    
    setUserResponses(updatedResponses);
    setCurrentInput('');
    
    if (currentQuestion < conversation.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Check if we should generate follow-up questions
      if (!followUpQuestionsGenerated) {
        // Generate follow-up questions before final narrative
        generateFollowUpQuestions(updatedResponses).then(success => {
          if (success) {
            setCurrentQuestion(conversation.length); // Move to the first follow-up question
          } else {
            // If follow-up generation fails, proceed to narrative
            generateNarrative(updatedResponses);
          }
        });
      } else {
        // All questions including follow-ups answered, generate narrative
        console.log('All questions answered, generating narrative');
        generateNarrative(updatedResponses);
      }
    }
  };

  // Function to initialize/rebuild conversation based on concerns
  const initializeConversation = (concerns) => {
    if (!concerns || concerns.length === 0) {
      setConversation([]);
      return;
    }

    console.log('Initializing conversation for concerns:', concerns);
    const initialMessages = concerns.flatMap(concern => {
      const concernQuestions = dynamicQuestions[concern] || FALLBACK_INITIAL_QUESTIONS[concern] || DEFAULT_QUESTIONS;
      return concernQuestions.map((q, i) => ({
        type: 'question',
        text: q,
        concern,
        questionIndex: i
      }));
    });
    console.log('Generated questions:', initialMessages);
    setConversation(initialMessages);
  };

  // Open edit concerns dialog
  const openEditDialog = () => {
    setDialogConcerns(formData.concerns || []);
    setEditDialogOpen(true);
  };

  // Close edit concerns dialog
  const closeEditDialog = () => {
    setEditDialogOpen(false);
  };

  // Handle changes in the dialog
  const handleDialogConcernChange = (event) => {
    const value = event.target.value;
    setDialogConcerns(typeof value === 'string' ? value.split(',') : value);
  };

  // Remove a specific concern
  const handleRemoveConcern = (concernToRemove) => {
    // Check if removing this concern would empty the list
    const updatedConcerns = (formData.concerns || []).filter(c => c !== concernToRemove);

    if (updatedConcerns.length === 0) {
      // If removing the last concern, confirm with the user
      if (window.confirm('Removing this concern will reset your progress as you need at least one concern. Continue?')) {
        handleSaveConcerns(updatedConcerns);
      }
    } else {
      // If at least one concern will remain, confirm and then update
      if (window.confirm('This will remove the concern and any answers related to it. Continue?')) {
        handleSaveConcerns(updatedConcerns);
      }
    }
  };

  // Save concerns from dialog
  const handleSaveConcerns = (newConcerns) => {
    // If concerns have changed
    if (JSON.stringify(newConcerns) !== JSON.stringify(formData.concerns)) {
      // If any concerns were removed, we need to clean up their responses
      const removedConcerns = formData.concerns.filter(c => !newConcerns.includes(c));
      let updatedResponses = { ...userResponses };

      // Remove responses for concerns that were removed
      removedConcerns.forEach(concern => {
        if (updatedResponses[concern]) {
          delete updatedResponses[concern];
        }
      });

      // Reset the conversation if concerns are completely changed
      if (newConcerns.length === 0 || removedConcerns.length === formData.concerns.length) {
        setConversation([]);
        setCurrentQuestion(0);
        setUserResponses({});
        setCurrentInput('');
        setNarrative('');
        setSubjectiveText('');
        setFollowUpQuestionsGenerated(false);
      } else {
        // Only rebuild the conversation for the updated concerns
        initializeConversation(newConcerns);
        setUserResponses(updatedResponses);
        // Clear any previously generated narrative/subjective summary
        setNarrative('');
        setSubjectiveText('');
        setFollowUpQuestionsGenerated(false); // Reset follow-up questions state when concerns change
        
        // Adjust current question if needed
        if (currentQuestion >= conversation.length) {
          setCurrentQuestion(0);
        }
      }

      // Update form data with new concerns
      onChange({
        ...formData,
        concerns: newConcerns,
        concerns_conversation: Object.keys(updatedResponses).length > 0 ? updatedResponses : null,
        medical_narrative: null // Clear previous narrative since concerns changed
      });
    }

    closeEditDialog();
  };

  const generateNarrative = async (responses) => {
    try {
      setIsGenerating(true);
      console.log('Generating narrative from responses:', responses);

      console.log('Sending narrative request to backend...');
      
      // Call secure backend endpoint instead of OpenAI directly
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/openai/generate-narrative`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          formData,
          responses
        })
      });

      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Backend API Error:', errorData);
        throw new Error(errorData.error || 'Failed to generate narrative');
      }
      
      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to generate narrative');
      }
      
      const data = result.data;
      console.log('API Response:', data);
      
      if (data.choices && data.choices[0]?.message?.content) {
        const generatedNarrative = data.choices[0].message.content;
        console.log('Generated narrative:', generatedNarrative);
        
        const cleanedNarrative = normalizeNarrativeText(generatedNarrative);
        console.log('Setting narrative state:', cleanedNarrative.substring(0, 50) + '...');
        setNarrative(cleanedNarrative);
        const subj = extractSubjective(cleanedNarrative);
        setSubjectiveText(subj);
        
        // Save both conversation responses and the generated narrative in parent component
        console.log('Updating form data with narrative');
        const updatedFormData = {
          ...formData,
          concerns_conversation: responses,
          medical_narrative: generatedNarrative
        };
        
        console.log('Updated form data:', updatedFormData);
        onChange(updatedFormData);
        
        // Force narrative display even if parent state update is delayed
        setTimeout(() => {
          console.log('Checking narrative state after update:', narrative);
          if (!narrative) {
            setNarrative(cleanedNarrative);
          }
        }, 500);
        console.log('Narrative generation complete');
      } else {
        console.error('Invalid API response format:', data);
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      console.error('Error generating narrative:', error);
      setNarrative(`Error: ${error.message}. Please try again.`);
    } finally {
      setIsGenerating(false);
    }
  };

  const normalizeNarrativeText = (text) => {
    if (!text) return '';
    let s = text.replace(/\r\n/g, '\n');
    s = s
      .replace(/\*{1,3}\s*(?:\d+\.?\s*)?(Subjective|Objective|Assessment|Plan)\s*[:\-]?\s*\*{1,3}/gi, '$1:')
      .replace(/^\s*#+\s*(?:\d+\.?\s*)?(Subjective|Objective|Assessment|Plan)\s*[:\-]?\s*$/gim, '$1:')
      .replace(/^\s*(?:\d+\.?\s*)?(Subjective|Objective|Assessment|Plan)\s*[:\-]?\s*$/gim, '$1:')
      .replace(/\*\*S\*\*\s*ubjective\s*[:\-]?/gi, 'Subjective:')
      .replace(/\*\*O\*\*\s*bjective\s*[:\-]?/gi, 'Objective:')
      .replace(/\*\*A\*\*\s*ssessment\s*[:\-]?/gi, 'Assessment:')
      .replace(/\*\*P\*\*\s*lan\s*[:\-]?/gi, 'Plan:');
    return s;
  };

  // Helper: extract Subjective section from SOAP narrative
  const extractSubjective = (text) => {
    if (!text) return '';
    let normalized = text;
    // Normalize various heading styles to a consistent "Heading:" format
    normalized = normalized
      // Bolded headings like **Subjective:** or **2. Objective:**
      .replace(/\*{1,3}\s*(?:\d+\.?\s*)?(Subjective|Objective|Assessment|Plan)\s*[:\-]?\s*\*{1,3}/gi, '$1:')
      // Markdown heading lines like ## 2. Plan
      .replace(/^\s*#+\s*(?:\d+\.?\s*)?(Subjective|Objective|Assessment|Plan)\s*[:\-]?\s*$/gim, '$1:')
      // Plain heading lines, optionally numbered: "2. Assessment"
      .replace(/^\s*(?:\d+\.?\s*)?(Subjective|Objective|Assessment|Plan)\s*[:\-]?\s*$/gim, '$1:');

    // Primary: explicit Subjective:
    const rxSubjective = /Subjective\s*:\s*([\s\S]*?)(?=\n[\s\*#>]*\d*\.?\s*(?:Objective|Assessment|Plan)\s*[:\-]?|\s*$)/i;
    const m1 = normalized.match(rxSubjective);
    if (m1 && m1[1]) return m1[1].trim();

    // Secondary: abbreviated S:
    const rxAbbrev = /\bS\s*:\s*([\s\S]*?)(?=\n[\s\*#>]*\d*\.?\s*\b(?:O|A|P)\s*[:\-]?|\s*$)/i;
    const m2 = normalized.match(rxAbbrev);
    if (m2 && m2[1]) return m2[1].trim();

    // Tertiary: heading line without colon, then content
    const rxNoColon = /\bSubjective\b\s*[\r\n]+([\s\S]*?)(?=\n[\s\*#>]*\d*\.?\s*(?:Objective|Assessment|Plan)\b|\s*$)/i;
    const m3 = normalized.match(rxNoColon);
    if (m3 && m3[1]) return m3[1].trim();

    // Fallback: limit to first paragraph to avoid dumping entire narrative
    const firstPara = normalized.split(/\n\s*\n/)[0] || '';
    return firstPara.trim().slice(0, 600);
  };

  // If no concerns selected yet, just show the selector
  if (conversation.length === 0) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            select
            required
            label="Check the conditions that you would like us to address during your consultation:"
            fullWidth
            SelectProps={{ multiple: true }}
            value={formData.concerns || []}
            onChange={e => onChange({ 
              ...formData,
              concerns: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value 
            })}
          >
            {concernOptions.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    );
  }

  // Get current question and user's previous responses
  const currentQ = conversation[currentQuestion];
  const concernProgress = formData.concerns?.map(concern => {
    const totalQuestions = conversation.filter(q => q.concern === concern).length;
    const answeredQuestions = Object.keys(userResponses[concern] || {}).length;
    return { concern, progress: Math.round((answeredQuestions / totalQuestions) * 100) };
  });
  
  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" color={fieldErrors.concerns ? 'error' : 'inherit'}>
            {t('labels.discussConcerns')}
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            size="small"
            onClick={openEditDialog}
          >
            {t('labels.editConcerns')}
          </Button>
        </Box>
        
        {fieldErrors.concerns && (
          <Typography color="error" variant="body2" sx={{ mb: 2 }}>
            {fieldErrors.concerns}
          </Typography>
        )}
        
        {/* Concerns as chips with remove option */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, border: fieldErrors.concerns ? '2px solid #d32f2f' : 'none', borderRadius: 1, p: fieldErrors.concerns ? 1 : 0 }}>
          {formData.concerns?.map((concern) => (
            <Chip 
              key={concern} 
              label={concern}
              onDelete={() => handleRemoveConcern(concern)}
              color="primary"
              variant="outlined"
              sx={{ mb: 1 }}
            />
          ))}
          {(!formData.concerns || formData.concerns.length === 0) && fieldErrors.concerns && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
              {t('messages.noConcernsSelected')}
            </Typography>
          )}
        </Box>
        
        {/* Progress indicators */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {concernProgress?.map(item => (
            <Box key={item.concern} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mr: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {item.concern}
              </Typography>
              <Box sx={{ width: '100%', bgcolor: '#e0e0e0', borderRadius: 1, height: 8, mt: 0.5 }}>
                <Box sx={{ width: `${item.progress}%`, bgcolor: 'primary.main', height: 8, borderRadius: 1 }} />
              </Box>
            </Box>
          ))}
        </Box>
        
        {isGeneratingQuestions ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress size={24} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              {t('messages.generatingQuestions')}
            </Typography>
          </Box>
        ) : currentQuestion < conversation.length ? (
          <>
            <Card variant="outlined" sx={{ mb: 2, bgcolor: currentQ?.type === 'follow-up' ? '#e3f2fd' : '#f5f5f5' }}>
              <CardContent>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  {t('labels.about')} {currentQ?.concern}
                  {currentQ?.type === 'follow-up' && (
                    <Typography component="span" variant="caption" sx={{ ml: 1, bgcolor: 'primary.light', color: 'white', p: 0.5, borderRadius: 1 }}>
                      {t('labels.followUp')}
                    </Typography>
                  )}
                </Typography>
                <Typography variant="h6">
                  {currentQ?.text}
                </Typography>
              </CardContent>
            </Card>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              placeholder={t('labels.typeResponse')}
              sx={{ mb: 2 }}
            />
            
            <Button
              variant="contained"
              onClick={handleResponseSubmit}
              disabled={!currentInput.trim()}
              fullWidth
            >
              {currentQuestion < conversation.length - 1 ? t('labels.nextQuestion') : t('labels.generateSummary')}
            </Button>

            {subjectiveText && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('labels.medicalSummary')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={subjectiveText}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            )}
          </>
        ) : isGenerating ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              {t('messages.generatingNarrative')}
            </Typography>
          </Box>
        ) : narrative && narrative.length > 0 ? (
          <Box mt={2}>
            {subjectiveText && (
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('labels.subjective')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={subjectiveText}
                  InputProps={{ readOnly: true }}
                />
              </Box>
            )}
            <Typography variant="h6" gutterBottom>
              {t('labels.medicalNarrative')}
            </Typography>
            <Box
              component="div"
              sx={{
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                whiteSpace: 'pre-wrap',
                backgroundColor: '#f9f9f9'
              }}
            >
              {narrative}
            </Box>
          </Box>
        ) : null}
        
        {/* Edit Concerns Dialog */}
        <Dialog open={editDialogOpen} onClose={closeEditDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {t('dialog.editTitle')}
            <IconButton
              aria-label="close"
              onClick={closeEditDialog}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('messages.selectConcernsDescription')}
            </Typography>
            <TextField
              select
              required
              label={t('labels.medicalConcerns')}
              fullWidth
              SelectProps={{ multiple: true }}
              value={dialogConcerns}
              onChange={handleDialogConcernChange}
            >
              {concernOptions.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditDialog}>{tCommon('buttons.cancel')}</Button>
            <Button onClick={() => handleSaveConcerns(dialogConcerns)} variant="contained" color="primary">
              {tCommon('buttons.save')}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </Grid>
  );
}